import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { useAppStore } from './store/useAppStore'
import MainLayout from './components/layout/MainLayout'
import ErrorBoundary from './components/common/ErrorBoundary'
import Spinner from './components/ui/Spinner'

// Lazy load pages
const Landing = lazy(() => import('./pages/Landing'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const AgentChat = lazy(() => import('./pages/AgentChat'))
const Settings = lazy(() => import('./pages/Settings'))
const Tickets = lazy(() => import('./pages/Tickets'))
const Admin = lazy(() => import('./pages/Admin'))
const Workflow = lazy(() => import('./pages/Workflow'))
const ProviderRegistration = lazy(() => import('./pages/ProviderRegistration'))
const ProviderDirectory = lazy(() => import('./pages/ProviderDirectory'))
const SkillsRegistry = lazy(() => import('./pages/SkillsRegistry'))
const ToolsRegistry = lazy(() => import('./pages/ToolsRegistry'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const Cookies = lazy(() => import('./pages/Cookies'))
const Demo = lazy(() => import('./pages/Demo'))
const Checkout = lazy(() => import('./pages/Checkout'))
const Billing = lazy(() => import('./pages/Billing'))
const DocumentHub = lazy(() => import('./pages/DocumentHub'))

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-night-950">
    <Spinner />
  </div>
)

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated)
  const user = useAppStore((state) => state.user)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (user?.role !== 'admin') return <Navigate to="/app/dashboard" replace />
  return <>{children}</>
}

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/provider-registration" element={<ProviderRegistration />} />
          <Route path="/providers" element={<ProviderDirectory />} />
          <Route path="/skills" element={<SkillsRegistry />} />
          <Route path="/tools" element={<ToolsRegistry />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/demo" element={<Demo />} />

          {/* Protected routes */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="agent/:agentType" element={<AgentChat />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="workflow" element={<Workflow />} />
            <Route path="skills" element={<SkillsRegistry />} />
            <Route path="tools" element={<ToolsRegistry />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="billing" element={<Billing />} />
            <Route path="documents" element={<DocumentHub />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Admin routes */}
          <Route
            path="/app/admin"
            element={
              <AdminRoute>
                <MainLayout />
              </AdminRoute>
            }
          >
            <Route index element={<Admin />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}

export default App
